import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import * as authApi from '../../utils/authUtils';
import { useAuth } from '../../contexts/AuthContext';

/**
 * AuthModal - A simplified, unified login/register modal.
 * Requirement: Login is register, register is login. Minimal UI, minimal logic.
 */
const AuthModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  returnPath?: string;
}> = ({ isOpen, onClose, returnPath }) => {
  const [account, setAccount] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showWechat, setShowWechat] = useState(false);

  const navigate = useNavigate();
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
  const wechatAppId = process.env.REACT_APP_WECHAT_APP_ID || '';

  // Clear state when opening
  useEffect(() => {
    if (isOpen) {
      setAccount('');
      setPassword('');
      setAuthError('');
      setIsLoading(false);
      setShowWechat(false);
    }
  }, [isOpen]);

  const [wechatSdkLoaded, setWechatSdkLoaded] = useState(false);
  const [wechatError, setWechatError] = useState(false);

  // Load WeChat SDK
  useEffect(() => {
    if (isOpen && showWechat && !(window as any).WxLogin && !wechatSdkLoaded) {
      const script = document.createElement('script');
      script.src = 'https://res.wx.qq.com/connect/zh_CN/htmledition/js/wxLogin.js';
      script.async = true;
      script.onload = () => setWechatSdkLoaded(true);
      document.body.appendChild(script);
      return () => {
        document.body.removeChild(script);
      };
    } else if ((window as any).WxLogin) {
      setWechatSdkLoaded(true);
    }
  }, [isOpen, showWechat, wechatSdkLoaded]);

  // Handle WeChat QR Code
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (isOpen && showWechat && wechatSdkLoaded && (window as any).WxLogin) {
      setWechatError(false);
      const redirectUri = encodeURIComponent(`${window.location.origin}/auth/wechat/callback`);
      
      try {
        new (window as any).WxLogin({
          id: 'wechat-login-container',
          appid: wechatAppId,
          scope: 'snsapi_login',
          redirect_uri: redirectUri,
          state: Math.random().toString(36).substring(2),
          style: 'black',
        });

        // Check for timeout if the container remains empty effectively (though we can't easily check iframe content due to CORS, getting here means code ran)
        // If the domain is invalid, WxLogin might run but fail to render the iframe properly or throw.
        // A simple timeout to show "If not loading..." message might be better.
        
        timeoutId = setTimeout(() => {
          const container = document.getElementById('wechat-login-container');
          if (container && container.innerHTML === '') {
             setWechatError(true);
          }
        }, 3500);

      } catch (err) {
        console.error("WeChat Login Init Error", err);
        setWechatError(true);
      }
    }
    return () => clearTimeout(timeoutId);
  }, [isOpen, showWechat, wechatSdkLoaded, wechatAppId]);

  const handleAuthSuccess = () => {
    window.dispatchEvent(new CustomEvent('authStateChange', { detail: { isAuthenticated: true, action: 'login' } }));
    onClose();
    navigate(returnPath || '/label-management');
  };

  const handlePasswordAuth = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!account || !password || isLoading) return;

    setIsLoading(true);
    try {
      setAuthError('');
      const res = await authApi.loginUser(account, password);
      if (res.success) {
        handleAuthSuccess();
      } else {
        setAuthError(res.message || '登录失败，请检查账号和密码');
      }
    } catch (err) {
      console.error('Auth error:', err);
      setAuthError('网络错误，请稍后重试');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = async (cred: any) => {
    if (!cred.credential || isLoading) return;
    setIsLoading(true);
    try {
      const res = await authApi.unifiedGoogleLogin(cred.credential);
      if (res.success) handleAuthSuccess();
    } catch (err) {
      console.error('Google auth error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/40 backdrop-blur-[2px] animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className="w-full max-w-[360px] bg-white rounded-[32px] p-8 shadow-2xl relative animate-in zoom-in-95 duration-200"
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-6 right-6 p-2 rounded-full hover:bg-gray-100 transition-colors"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>


        <div className="text-center mb-8">
          <h2 className="text-2xl font-black text-gray-900">登录 / 注册</h2>
          <p className="text-sm text-gray-400 mt-2">欢迎来到 SSEEAA</p>
        </div>

        {!showWechat ? (
          <form onSubmit={handlePasswordAuth} className="space-y-3">
            <input
              type="text"
              placeholder="手机号 / 用户名"
              value={account}
              onChange={e => {
                setAccount(e.target.value);
                if (authError) setAuthError('');
              }}
              className="w-full h-14 px-6 rounded-2xl bg-gray-50 border-2 border-transparent focus:border-blue-500 focus:bg-white outline-none transition-all font-medium"
            />
            <input
              type="password"
              placeholder="密码"
              value={password}
              onChange={e => {
                setPassword(e.target.value);
                if (authError) setAuthError('');
              }}
              className="w-full h-14 px-6 rounded-2xl bg-gray-50 border-2 border-transparent focus:border-blue-500 focus:bg-white outline-none transition-all font-medium"
            />
            {authError && (
              <p className="text-sm text-red-500 px-1">{authError}</p>
            )}
            <button
              type="submit"
              disabled={isLoading || !account || !password}
              className="w-full h-14 bg-blue-600 text-white rounded-2xl font-bold hover:bg-blue-700 active:scale-95 transition-all disabled:opacity-50 disabled:active:scale-100 mt-2"
            >
              {isLoading ? '处理中...' : '继续'}
            </button>
          </form>
        ) : (
          <div className="w-full flex flex-col items-center animate-in fade-in slide-in-from-right-4 duration-300">
             {wechatError ? (
               <div className="flex flex-col items-center justify-center h-[300px] gap-4 text-center">
                 <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center text-red-500 mb-2">
                   <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="8" x2="12" y2="12"></line>
                      <line x1="12" y1="16" x2="12.01" y2="16"></line>
                   </svg>
                 </div>
                 <p className="text-gray-600 font-medium">二维码加载失败</p>
                 <p className="text-xs text-gray-400 max-w-[200px]">可能是网络问题或域名未注册 (Localhost)</p>
                 <button 
                   onClick={() => setWechatError(false)}
                   className="text-sm text-blue-600 font-bold hover:underline"
                 >
                   重试
                 </button>
               </div>
             ) : (
                <div id="wechat-login-container" className="min-h-[300px] flex items-center justify-center">
                  {!wechatSdkLoaded && <div className="animate-pulse text-gray-400 text-sm">正在加载微信服务...</div>}
                </div>
             )}
             
             <button
               onClick={() => setShowWechat(false)}
               className="mt-4 px-6 py-2 rounded-xl bg-gray-50 text-gray-600 text-sm font-bold hover:bg-gray-100 transition-colors flex items-center gap-2"
             >
               <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M19 12H5M12 19l-7-7 7-7" />
               </svg>
               返回账号登录
             </button>
          </div>
        )}

        {!showWechat && (
          <div className="mt-8 pt-8 border-t border-gray-100 flex flex-col items-center gap-4">
            {googleClientId && (
              <GoogleOAuthProvider clientId={googleClientId}>
                <div className="w-full flex justify-center">
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={() => console.error('Google Login Failed')}
                    size="large"
                    shape="pill"
                    text="continue_with"
                    width="280"
                  />
                </div>
              </GoogleOAuthProvider>
            )}

            {wechatAppId && (
              <button
                onClick={() => setShowWechat(true)}
                className="flex items-center gap-2 text-sm font-bold text-green-600 hover:opacity-80 transition-opacity"
              >
                <img src="/images/service.svg" className="w-5 h-5" alt="" />
                微信扫码登录
              </button>
            )}
          </div>
        )}
      </div>

    </div>
  );
};
export default AuthModal;

/**
 * GlobalAuthModals - Mounts the AuthModal globally and handles its open/close state.
 */
export const GlobalAuthModals = () => {
  const { modal, closeAuthModal } = useAuth();
  return (
    <AuthModal
      isOpen={modal.isOpen}
      onClose={closeAuthModal}
      returnPath={modal.returnPath}
    />
  );
};
